/**
 * Essence abilities (#145, docs/gameplay/skill-definitions.md, Essence
 * Abilities and Weave). Every Essence skill grants one ability, cast from
 * the action bar: it spends from the essence pool and has a cooldown. The
 * host runs each ability's effect (crucible-host `essence-ability-effects`).
 * Numbers are first-pass, to tune.
 */

import { ESSENCE_EXCHANGE_RATE } from './essence-exchange';
import { assertNever } from './utils';
import { findSkillCatalogEntry } from './skill-catalog';
import { WEAVE_ABILITY_COUNT } from './skill-perks';
import { resolveLoadoutBehaviors } from './behaviors';
import type { AbilityChange, EveryAbilityChange } from './types/ability-changes';
import type { ActiveBehavior } from './types/behaviors';
import type { EssenceType } from './types/essence';
import type { AbilitySoundSet } from './types/sound';
import type { CharacterEquipmentLoadout, CombatModifierState, EssenceStatusKind, Skill } from './types/game';
import { resolveAbilityChanges } from './item-behaviors';
import { resolveItemAbilityCooldownReduction, resolveItemAbilityCostReduction } from './item-instances';

/** The trait an essence type lends the other ability in a woven pair. */
export type WeaveTrait = 'spread' | 'steel' | 'mend' | 'linger' | 'pierce' | 'return';

export const WEAVE_TRAIT_BY_ESSENCE_TYPE: Readonly<Record<EssenceType, WeaveTrait>> = {
    fury: 'spread',
    courage: 'steel',
    sorrow: 'mend',
    memory: 'linger',
    truth: 'pierce',
    resonance: 'return',
};

/** A woven pair costs this share of casting both apart. */
export const WEAVE_COST_FRACTION = 0.8;

/**
 * Who an ability is cast on. `enemy` and `ally` need a target in range
 * (an `ally` is never the caster); `self` abilities act on or around the
 * caster; `doorway` lands on the doorway the caster faces, in range;
 * `aimed` needs no target: it goes off toward the enemy under the cursor,
 * if one is in range, or else toward where the caster aims.
 */
export type EssenceAbilityTargeting = 'self' | 'enemy' | 'ally' | 'doorway' | 'aimed';

/** Whether an ability harms enemies or helps allies: it decides which side of a Weave trait applies. */
export type EssenceAbilityNature = 'harmful' | 'helpful';

/** The damage an ability deals per hit: rolled between these, then scaled by power. */
export interface AbilityDamageRange {
    readonly min: number;
    readonly max: number;
}

/**
 * The status an ability puts on whoever it reaches. Power (Overcharge, an
 * Echo repeat) scales it as each field says; perks and items change it
 * through `AbilityChange`.
 */
export interface AbilityStatusEffect {
    readonly status: EssenceStatusKind;
    readonly durationMs: number;
    /** Power lengthens it rather than strengthening it (Intercede, Nullify). */
    readonly isPowerInLength?: boolean;
    /** Combat modifiers it adds, times power. */
    readonly modifiers?: CombatModifierState;
    /** What it costs its caster, on the caster alone and never scaled (Frenzy's damage taken). */
    readonly drawback?: CombatModifierState;
    /** Attack cooldowns while it lasts: shortened by (1 - this) times power, to half at most. */
    readonly attackCooldownFactor?: number;
    /** Its strength (`EssenceStatus.magnitude`), times power, up to `maxMagnitude`. */
    readonly magnitude?: number;
    readonly maxMagnitude?: number;
    /** Its magnitude is damage a second: it ticks as damage over time, and Spread passes on only its damage share. */
    readonly isMagnitudeDamage?: boolean;
    /** Hits it has: Smite's charged hits, Nullify's absorbs. */
    readonly charges?: number;
    /** Told to each recipient other than the caster; `{caster}` is the caster's name. */
    readonly recipientMessage?: string;
}

/**
 * What an ability does when it goes off. Each kind is one behavior on the
 * host (crucible-host `essence-ability-effects`); an ability is its kind and
 * its numbers, and a new one of an existing kind needs no code.
 */
export type EssenceAbilityEffect =
    /** A status on the caster, and with Spread on allies near them. */
    | { readonly kind: 'self-status'; readonly status: AbilityStatusEffect }
    /** A status on its target (an ally when helpful, an enemy when harmful), and with Spread on others near it. */
    | { readonly kind: 'target-status'; readonly status: AbilityStatusEffect }
    /** A burst around the caster: damage to the enemies in it (allies take `allyDamageFraction` of it), a status on them, or both. */
    | {
        readonly kind: 'burst';
        readonly radiusTiles: number;
        readonly damage?: AbilityDamageRange;
        readonly allyDamageFraction?: number;
        readonly status?: AbilityStatusEffect;
    }
    /** A bolt flying where it was aimed, after gathering in the hand; its hit is rolled on the first enemy it reaches. */
    | {
        readonly kind: 'projectile';
        readonly damage: AbilityDamageRange;
        readonly gatherMs: number;
        /** Tiles a second. */
        readonly speed: number;
        readonly radiusTiles: number;
        /** How the bolt looks: its entry in the projectile catalog (#335). */
        readonly projectileId: string;
    }
    /** Restores a share of its target's pool; anyone else it reaches gets `splashFraction` of that. */
    | { readonly kind: 'heal'; readonly poolFraction: number; readonly splashFraction: number }
    /** A channel that restores the caster in pulses (Draw); its numbers are the `DRAW_*` rules. */
    | { readonly kind: 'channel' }
    /** Holds the doorway it lands on this long (`held-door.ts`). */
    | { readonly kind: 'doorway'; readonly durationMs: number }
    /**
     * Marks the caster for the casts that follow (Overcharge, Echo, Weave);
     * the cast service reads the mark. `hasSockets`: it opens a socket for
     * each ability it blends.
     */
    | {
        readonly kind: 'cast-mark';
        readonly status: 'overcharge' | 'echo' | 'weave';
        readonly windowMs: number;
        readonly magnitude?: number;
        readonly hasSockets?: boolean;
    };

/** Every Essence ability's id, as `ESSENCE_ABILITIES` lists them (a test keeps the two in step), so code naming one is checked. */
export const ESSENCE_ABILITY_IDS = [
    'bolt-of-wrath', 'frenzy', 'immolate', 'instinct', 'intercede', 'smite', 'solace', 'blight',
    'held-door', 'study', 'echo', 'unveil', 'nullify', 'draw', 'overcharge', 'weave',
] as const;

export type EssenceAbilityId = typeof ESSENCE_ABILITY_IDS[number];

export interface EssenceAbilityDefinition {
    readonly id: string;
    /** The Essence skill that grants it. */
    readonly skillId: string;
    readonly name: string;
    readonly description: string;
    /** Essence spent from the pool on each cast. 0 for a free ability, which earns no Attunement. */
    readonly essenceCost: number;
    readonly cooldownMs: number;
    readonly targeting: EssenceAbilityTargeting;
    /** How far a targeted ability reaches, in tiles. */
    readonly rangeTiles?: number;
    readonly nature: EssenceAbilityNature;
    /**
     * Held to cast: the hotkey must be held this long, and letting go first
     * cancels with nothing spent. Abilities without it cast on the press.
     */
    readonly holdToCastMs?: number;
    /**
     * Press and trigger: the cast starts on the press, its cast clip with it,
     * and the ability goes off this long after, at the clip's peak.
     * Abilities without it go off on the press. Nothing is spent until it goes
     * off, and a caster who falls first casts nothing. Cast into an open Weave
     * it is only queued, with no windup.
     */
    readonly windupMs?: number;
    /** Its sound cues (game-content/data/sounds.json ids); any left out play the generic Essence cues. */
    readonly sounds?: AbilitySoundSet;
    readonly effect: EssenceAbilityEffect;
}

// Weave traits (Harmony). Concord makes lent traits half again as strong.
/** Spread: a harmful effect splashes to enemies this close to its target, for this share. */
export const SPREAD_RADIUS_TILES = 2;
export const SPREAD_DAMAGE_FRACTION = 0.5;
/** Spread: a helpful effect also reaches allies this close to its target. */
export const SPREAD_ALLY_RADIUS_TILES = 3;
/** Steel: the caster takes this much less damage for a moment. */
export const STEEL_DAMAGE_TAKEN = -0.2;
export const STEEL_DURATION_MS = 4000;
/** Mend: a harmful effect heals the caster for this share of the damage it deals. */
export const MEND_DAMAGE_HEAL_FRACTION = 0.25;
/** Mend: a heal heals this much more; another helpful effect also heals its target this share of their pool. */
export const MEND_HEAL_BONUS = 0.25;
export const MEND_HELPFUL_HEAL_FRACTION = 0.05;
/** Linger: timed effects last this much longer. */
export const LINGER_DURATION_BONUS = 0.5;
/** Pierce: a harmful effect ignores this share of mitigation and unveils its target for a moment. */
export const PIERCE_MITIGATION_IGNORED = 0.5;
export const PIERCE_UNVEIL_MS = 6000;
/** Return: gives back this share of the ability's part of the cost. */
export const RETURN_REFUND_FRACTION = 0.25;

/** How long Weave waits for the abilities it blends. */
export const WEAVE_WINDOW_MS = 10_000;
/** A full Weave shows its caster every socket filled this long before its abilities go off together. */
export const WEAVE_RELEASE_DELAY_MS = 250;

// Fury
/** Bolt of Wrath: a weak essence hit, cheap and quick enough to fire again and again. */
export const BOLT_OF_WRATH_DAMAGE = { min: 4, max: 6 } as const;
/**
 * Bolt of Wrath flies straight toward where it was aimed, tracking no one:
 * it gathers in the hand this long, then flies at this speed (tiles a
 * second) to the end of its reach, stopped by a wall. Its hit is rolled on
 * the first enemy it reaches; a miss flies on past them.
 */
export const BOLT_OF_WRATH_GATHER_MS = 200;
export const BOLT_OF_WRATH_SPEED = 16;
/** The bolt's body, in tiles. */
export const BOLT_OF_WRATH_RADIUS_TILES = 0.2;
/** Frenzy: damage dealt, attack speed, and the damage taken it costs. */
export const FRENZY_DURATION_MS = 8000;
export const FRENZY_DAMAGE_BONUS = 0.2;
export const FRENZY_ATTACK_COOLDOWN_FACTOR = 0.85;
export const FRENZY_DAMAGE_TAKEN = 0.15;
/** Immolate: a burst around the caster. Allies caught in it take a share. */
export const IMMOLATE_DAMAGE = { min: 10, max: 14 } as const;
export const IMMOLATE_RADIUS_TILES = 2.5;
export const IMMOLATE_ALLY_DAMAGE_FRACTION = 1 / 3;

// Courage
/** Instinct: harder to hit by every kind of attack. */
export const INSTINCT_DURATION_MS = 5000;
export const INSTINCT_AVOIDANCE_BONUS = 25;
/** Intercede: the ally's damage comes out of the caster's pool. */
export const INTERCEDE_DURATION_MS = 5000;
/** Intercede breaks when the ally is this far from the caster. */
export const INTERCEDE_BREAK_RANGE_TILES = 8;
/** Smite: the next weapon hit within the window deals more and can't be blocked. */
export const SMITE_WINDOW_MS = 6000;
export const SMITE_DAMAGE_BONUS = 0.5;

// Sorrow
/** Solace: restores a share of the target's pool. */
/** How far Solace reaches an ally, in tiles. */
export const SOLACE_RANGE_TILES = 6;
export const SOLACE_HEAL_FRACTION = 0.2;
/** Solace: anyone else it reaches (Spread, Lifeline) gets this share of the target's heal. */
export const SOLACE_SPLASH_HEAL_FRACTION = 0.5;
/** Blight: essence damage each second, ignoring avoidance and mitigation. */
export const BLIGHT_DURATION_MS = 8000;
export const BLIGHT_DAMAGE_PER_SECOND = 3;
/**
 * The Held Door: a doorway the caster faces, within reach and this cone, is
 * held for a while. Enemies coming through it toward the caster's side wait
 * their turn, one at a time, the weaker the longer (see `held-door.ts`).
 */
export const HELD_DOOR_DURATION_MS = 6000;
export const HELD_DOOR_RANGE_TILES = 8;
export const HELD_DOOR_CONE_DEGREES = 60;
export const HELD_DOOR_HOLD_TO_CAST_MS = 1000;
/** An enemy waits this long for each level the caster's Parting stands above it, plus one. */
export const HELD_DOOR_WAIT_PER_LEVEL_MS = 1000;
/** An elite weighs this many levels more than its kind. */
export const HELD_DOOR_ELITE_LEVEL_BONUS = 2;

// Memory
/** Study: party hits on the studied enemy can strike weak points. */
export const STUDY_DURATION_MS = 10_000;
export const STUDY_WEAK_POINT_CHANCE = 0.35;
/** Study's weak-point chance never rises past this, however strong the cast. */
export const STUDY_MAX_WEAK_POINT_CHANCE = 0.9;
/** Echo: the next ability within the window repeats, after a beat, at this strength. */
export const ECHO_WINDOW_MS = 8000;
export const ECHO_REPEAT_DELAY_MS = 1000;
export const ECHO_REPEAT_POWER = 0.5;

// Truth
/** Unveil: enemies near the caster are easier for the party to hit. */
export const UNVEIL_DURATION_MS = 8000;
export const UNVEIL_RADIUS_TILES = 6;
export const UNVEIL_AVOIDANCE_PENALTY = 20;
/** Nullify: a ward that absorbs the next hit. */
export const NULLIFY_DURATION_MS = 6000;

// Resonance
/** The Essence skill that grants Draw; an action-bar slot that holds Draw points at it. */
export const DRAW_SKILL_ID = 'attune';
/**
 * Draw: how a character recovers in a run (there is no passive
 * regeneration). A channel the caster moves freely through that restores a
 * share of the pool in pulses: each pulse drinks from the caster's own
 * essence orb close by, up to what the Draw has left, or else restores its
 * share. It ends once the last pulse lands.
 */
export const DRAW_PULSES = 6;
/** How long one pulse takes while the caster stands still. */
export const DRAW_PULSE_MS = 1000;
/** A pulse's speed while the caster moves, attacks or casts: half, so each takes twice as long. */
export const DRAW_ACTING_PULSE_RATE = 0.5;
/** How close an orb must be, in tiles, for a pulse to drink from it. */
export const DRAW_ORB_REACH_TILES = 2.5;
/** How long after an attack or cast the caster still counts as acting. */
export const DRAW_ACTING_GRACE_MS = 1000;
/** The shortest a Draw lasts: every pulse at full speed. */
export const DRAW_DURATION_MS = DRAW_PULSES * DRAW_PULSE_MS;
/** Draw's restore, a share of the pool: this much, plus `DRAW_RESTORE_PER_VITALITY` a point of Vitality (5% for a new character). */
export const DRAW_RESTORE_BASE_FRACTION = 0.04;
export const DRAW_RESTORE_PER_VITALITY = 0.005;
/**
 * What a point of orb essence refills while drawing, per point of its
 * exchange value (`ESSENCE_EXCHANGE_RATE`): Resonance 0.25 up to Truth 2.
 * The rarer the essence, the more you give up to drink it.
 */
export const DRAW_ORB_HEAL_PER_VALUE = 0.25;

/** Draw's restore at this Vitality, plus any item bonus (Flowing), as a share of the pool. */
export function resolveDrawRestoreFraction(vitality: number, itemBonus = 0): number {
    return DRAW_RESTORE_BASE_FRACTION + DRAW_RESTORE_PER_VITALITY * vitality + itemBonus;
}

/** What one point of `type` essence from an orb refills while drawing; Veil Attunement adds its percent. */
export function resolveDrawOrbHealPerPoint(type: EssenceType, veilAttunementPercent = 0): number {
    return ESSENCE_EXCHANGE_RATE[type] * DRAW_ORB_HEAL_PER_VALUE * (1 + Math.max(0, veilAttunementPercent) / 100);
}
/** Draw: drawing leaves the caster exposed. */
export const DRAW_DAMAGE_TAKEN = 0.2;
/** Overcharge: the next ability within the window is this much stronger. */
export const OVERCHARGE_WINDOW_MS = 10_000;
export const OVERCHARGE_POWER = 1.5;

/** Every Essence ability in the game, one per Essence skill. */
export const ESSENCE_ABILITIES: readonly EssenceAbilityDefinition[] = [
    {
        id: 'bolt-of-wrath',
        skillId: 'wrath',
        name: 'Bolt of Wrath',
        description: 'Hurl a bolt of raw fury where you aim: an essence hit for 4-6 damage on the first enemy in its path.',
        essenceCost: 1,
        cooldownMs: 600,
        targeting: 'aimed',
        rangeTiles: 8,
        nature: 'harmful',
        effect: { kind: 'projectile', damage: BOLT_OF_WRATH_DAMAGE, gatherMs: BOLT_OF_WRATH_GATHER_MS, speed: BOLT_OF_WRATH_SPEED, radiusTiles: BOLT_OF_WRATH_RADIUS_TILES, projectileId: 'wrath-bolt' },
    },
    {
        id: 'frenzy',
        skillId: 'frenzy',
        name: 'Frenzy',
        description: 'For 8s, deal 20% more damage and attack 15% faster, but take 15% more damage.',
        essenceCost: 14,
        cooldownMs: 20_000,
        targeting: 'self',
        nature: 'helpful',
        windupMs: 800,
        effect: {
            kind: 'self-status',
            status: {
                status: 'frenzy',
                durationMs: FRENZY_DURATION_MS,
                modifiers: { damageMultiplier: FRENZY_DAMAGE_BONUS },
                drawback: { damageTakenMultiplier: FRENZY_DAMAGE_TAKEN },
                attackCooldownFactor: FRENZY_ATTACK_COOLDOWN_FACTOR,
            },
        },
    },
    {
        id: 'immolate',
        skillId: 'immolation',
        name: 'Immolate',
        description: 'Burst into flame: 10-14 damage to every enemy within 2.5 tiles. Allies caught in it take a third.',
        essenceCost: 18,
        cooldownMs: 10_000,
        targeting: 'self',
        nature: 'harmful',
        windupMs: 800,
        effect: { kind: 'burst', radiusTiles: IMMOLATE_RADIUS_TILES, damage: IMMOLATE_DAMAGE, allyDamageFraction: IMMOLATE_ALLY_DAMAGE_FRACTION },
    },
    {
        id: 'instinct',
        skillId: 'battle-instincts',
        name: 'Instinct',
        description: 'For 5s, read every blow: much harder to hit (+25 avoidance against every attack).',
        essenceCost: 12,
        cooldownMs: 15_000,
        targeting: 'self',
        nature: 'helpful',
        effect: { kind: 'self-status', status: { status: 'instinct', durationMs: INSTINCT_DURATION_MS, modifiers: { avoidanceBonus: INSTINCT_AVOIDANCE_BONUS } } },
    },
    {
        id: 'intercede',
        skillId: 'valor',
        name: 'Intercede',
        description: 'For 5s, the damage an ally would take comes out of your essence pool instead. It can fell you.',
        essenceCost: 14,
        cooldownMs: 20_000,
        targeting: 'ally',
        rangeTiles: 6,
        nature: 'helpful',
        effect: { kind: 'target-status', status: { status: 'intercede', durationMs: INTERCEDE_DURATION_MS, isPowerInLength: true, recipientMessage: '{caster} intercedes for you.' } },
    },
    {
        id: 'smite',
        skillId: 'smite',
        name: 'Smite',
        description: 'Charge your weapon: your next hit within 6s deals 50% more and can\'t be blocked.',
        essenceCost: 10,
        cooldownMs: 8000,
        targeting: 'self',
        nature: 'helpful',
        effect: { kind: 'self-status', status: { status: 'smite', durationMs: SMITE_WINDOW_MS, magnitude: SMITE_DAMAGE_BONUS, charges: 1 } },
    },
    {
        id: 'solace',
        skillId: 'restoration',
        name: 'Solace',
        description: 'Restore 20% of an ally\'s essence pool.',
        essenceCost: 17,
        cooldownMs: 10_000,
        targeting: 'ally',
        rangeTiles: SOLACE_RANGE_TILES,
        nature: 'helpful',
        effect: { kind: 'heal', poolFraction: SOLACE_HEAL_FRACTION, splashFraction: SOLACE_SPLASH_HEAL_FRACTION },
    },
    {
        id: 'blight',
        skillId: 'entropy',
        name: 'Blight',
        description: 'Rot an enemy: 3 damage a second for 8s, past its avoidance and armor.',
        essenceCost: 12,
        cooldownMs: 8000,
        targeting: 'enemy',
        rangeTiles: 8,
        nature: 'harmful',
        effect: { kind: 'target-status', status: { status: 'blight', durationMs: BLIGHT_DURATION_MS, magnitude: BLIGHT_DAMAGE_PER_SECOND, isMagnitudeDamage: true } },
    },
    {
        id: 'held-door',
        skillId: 'parting',
        name: 'The Held Door',
        description: 'Hold to raise. For 6s, the doorway you face within 8 tiles lets enemies through toward you one at a time. Each waits a second for every level your Parting stands above it, plus one; stronger enemies don\'t wait.',
        essenceCost: 19,
        cooldownMs: 25_000,
        targeting: 'doorway',
        rangeTiles: HELD_DOOR_RANGE_TILES,
        nature: 'harmful',
        holdToCastMs: HELD_DOOR_HOLD_TO_CAST_MS,
        effect: { kind: 'doorway', durationMs: HELD_DOOR_DURATION_MS },
    },
    {
        id: 'study',
        skillId: 'insight',
        name: 'Study',
        description: 'Study an enemy for 10s: your party\'s hits on it strike weak points 35% of the time.',
        essenceCost: 10,
        cooldownMs: 15_000,
        targeting: 'enemy',
        rangeTiles: 10,
        nature: 'harmful',
        effect: { kind: 'target-status', status: { status: 'study', durationMs: STUDY_DURATION_MS, magnitude: STUDY_WEAK_POINT_CHANCE, maxMagnitude: STUDY_MAX_WEAK_POINT_CHANCE } },
    },
    {
        id: 'echo',
        skillId: 'echo',
        name: 'Echo',
        description: 'The next ability you cast within 8s repeats a second later at half strength, for free.',
        essenceCost: 12,
        cooldownMs: 20_000,
        targeting: 'self',
        nature: 'helpful',
        effect: { kind: 'cast-mark', status: 'echo', windowMs: ECHO_WINDOW_MS },
    },
    {
        id: 'unveil',
        skillId: 'warding',
        name: 'Unveil',
        description: 'See true: for 8s, enemies within 6 tiles are easier for your party to hit (-20 avoidance).',
        essenceCost: 12,
        cooldownMs: 18_000,
        targeting: 'self',
        nature: 'harmful',
        effect: {
            kind: 'burst',
            radiusTiles: UNVEIL_RADIUS_TILES,
            status: { status: 'unveiled', durationMs: UNVEIL_DURATION_MS, modifiers: { avoidanceBonus: -UNVEIL_AVOIDANCE_PENALTY } },
        },
    },
    {
        id: 'nullify',
        skillId: 'veilguard',
        name: 'Nullify',
        description: 'A ward for 6s that absorbs the next hit you take.',
        essenceCost: 10,
        cooldownMs: 12_000,
        targeting: 'self',
        nature: 'helpful',
        effect: { kind: 'self-status', status: { status: 'nullify', durationMs: NULLIFY_DURATION_MS, isPowerInLength: true, charges: 1 } },
    },
    {
        id: 'draw',
        skillId: DRAW_SKILL_ID,
        name: 'Draw',
        description: 'Draw essence back in six pulses, a second each while you stand still and two while you move or act: restore 5% of your pool, drinking from your essence orbs close by first. Cast again to stop. You take 20% more damage while drawing, and its cooldown starts when it ends.',
        essenceCost: 0,
        cooldownMs: 20_000,
        targeting: 'self',
        nature: 'helpful',
        effect: { kind: 'channel' },
    },
    {
        id: 'overcharge',
        skillId: 'channel',
        name: 'Overcharge',
        description: 'Pour in extra essence: the next ability you cast within 10s is 50% stronger.',
        essenceCost: 12,
        cooldownMs: 15_000,
        targeting: 'self',
        nature: 'helpful',
        effect: { kind: 'cast-mark', status: 'overcharge', windowMs: OVERCHARGE_WINDOW_MS, magnitude: OVERCHARGE_POWER },
    },
    {
        id: 'weave',
        skillId: 'harmony',
        name: 'Weave',
        description: 'The next two abilities you cast within 10s, from different essence types, go off together, each with a trait lent by the other, for 80% of their cost.',
        essenceCost: 7,
        cooldownMs: 12_000,
        targeting: 'self',
        nature: 'helpful',
        effect: { kind: 'cast-mark', status: 'weave', windowMs: WEAVE_WINDOW_MS, hasSockets: true },
    },
];

/** The Essence ability with id `abilityId`; undefined for an id that isn't one (an item behavior). */
export function findEssenceAbility(
    abilityId: string,
    abilities: readonly EssenceAbilityDefinition[] = ESSENCE_ABILITIES,
): EssenceAbilityDefinition | undefined {
    return abilities.find((ability) => ability.id === abilityId);
}

/** The ability `skillId` grants. */
export function findEssenceAbilityForSkill(
    skillId: string,
    abilities: readonly EssenceAbilityDefinition[] = ESSENCE_ABILITIES,
): EssenceAbilityDefinition | undefined {
    return abilities.find((ability) => ability.skillId === skillId);
}

/** Whether `ability` is a channel that goes on after its cast (Draw): its cooldown starts when the channel ends, and the caster plays no cast motion for it. */
export function isChannelAbility(ability: EssenceAbilityDefinition): boolean {
    return ability.effect.kind === 'channel';
}

/** Whether `ability` marks the casts that follow (Overcharge, Echo, Weave) rather than being one the mark applies to. */
export function isCastMarkAbility(ability: EssenceAbilityDefinition): boolean {
    return ability.effect.kind === 'cast-mark';
}

/** Perks' behaviors before named items', so an item's override wins (as it always has). */
function listPerksThenItems(skills: readonly Skill[] | undefined, equipped: CharacterEquipmentLoadout | undefined): ActiveBehavior[] {
    const behaviors = resolveLoadoutBehaviors(skills, equipped);
    return [...behaviors.filter(({ source }) => source.kind === 'perk'), ...behaviors.filter(({ source }) => source.kind === 'item')];
}

/**
 * How `ability` is changed for a character (`ability-modifier` behaviors
 * for it): the changes of every perk they have unlocked, then their
 * wielded named items'. Changes to every ability are separate
 * (`resolveEveryAbilityChange`).
 */
export function resolveAbilityChange(
    ability: EssenceAbilityDefinition,
    skills: readonly Skill[] | undefined,
    equipped?: CharacterEquipmentLoadout,
): AbilityChange {
    return listPerksThenItems(skills, equipped).reduce<AbilityChange>(
        (total, { behavior }) => (behavior.kind === 'ability-modifier' && behavior.abilityId === ability.id
            ? resolveAbilityChanges(total, behavior.change)
            : total),
        {},
    );
}

/** How every Essence ability is changed for a character (`every-ability-modifier` behaviors: Efficient, Lingering). */
export function resolveEveryAbilityChange(
    skills: readonly Skill[] | undefined,
    equipped?: CharacterEquipmentLoadout,
): EveryAbilityChange {
    return listPerksThenItems(skills, equipped).reduce<EveryAbilityChange>(
        (total, { behavior }) => (behavior.kind === 'every-ability-modifier' ? resolveAbilityChanges(total, behavior.change) : total),
        {},
    );
}

/** The status an ability puts on whoever it reaches, if any. */
function findAbilityStatusKind(ability: EssenceAbilityDefinition): EssenceStatusKind | undefined {
    const { effect } = ability;
    switch (effect.kind) {
        case 'self-status':
        case 'target-status':
            return effect.status.status;
        case 'burst':
            return effect.status?.status;
        case 'cast-mark':
            return effect.status;
        case 'projectile':
        case 'heal':
        case 'channel':
        case 'doorway':
            return undefined;
        default:
            return assertNever(effect);
    }
}

/**
 * Combat modifiers a character's perks and named items add to every status
 * of `statusKind` they put on anyone, however it gets there: the
 * `statusModifiers` change of the ability whose own status it is
 * (Unmasked, on Unveil's: every enemy the character unveils).
 */
export function resolveAddedStatusModifiers(
    statusKind: EssenceStatusKind,
    skills: readonly Skill[] | undefined,
    equipped?: CharacterEquipmentLoadout,
): CombatModifierState | undefined {
    const owner = ESSENCE_ABILITIES.find((ability) => findAbilityStatusKind(ability) === statusKind);
    return owner ? resolveAbilityChange(owner, skills, equipped).statusModifiers : undefined;
}

/** The ability that marks the casts after it with `status` (Echo, Weave): its changes say how the mark works. */
function findCastMarkAbility(status: EssenceStatusKind): EssenceAbilityDefinition | undefined {
    return ESSENCE_ABILITIES.find((ability) => ability.effect.kind === 'cast-mark' && ability.effect.status === status);
}

function findWeaveAbility(): EssenceAbilityDefinition | undefined {
    return findCastMarkAbility('weave');
}

/** How many abilities a character's Weave blends: `WEAVE_ABILITY_COUNT`, more with Unison. */
export function countWeaveAbilities(skills: readonly Skill[] | undefined, equipped?: CharacterEquipmentLoadout): number {
    const weave = findWeaveAbility();
    return WEAVE_ABILITY_COUNT + (weave ? resolveAbilityChange(weave, skills, equipped).extraWovenAbilities ?? 0 : 0);
}

/** The share of casting them apart a character's woven abilities cost: `WEAVE_COST_FRACTION`, less with Blend. */
export function resolveWeaveCostFraction(skills: readonly Skill[] | undefined, equipped?: CharacterEquipmentLoadout): number {
    const weave = findWeaveAbility();
    return (weave ? resolveAbilityChange(weave, skills, equipped).weaveCostFraction : undefined) ?? WEAVE_COST_FRACTION;
}

/** How strong the traits a character's Weave lends are: 1, more with Concord. */
export function resolveWeaveTraitStrength(skills: readonly Skill[] | undefined, equipped?: CharacterEquipmentLoadout): number {
    const weave = findWeaveAbility();
    return (weave ? resolveAbilityChange(weave, skills, equipped).traitStrengthMultiplier : undefined) ?? 1;
}

/** The strength a character's Echo repeats an ability at: `ECHO_REPEAT_POWER`, full with Recall. */
export function resolveEchoRepeatPower(skills: readonly Skill[] | undefined, equipped?: CharacterEquipmentLoadout): number {
    const echo = findCastMarkAbility('echo');
    return (echo ? resolveAbilityChange(echo, skills, equipped).repeatPower : undefined) ?? ECHO_REPEAT_POWER;
}

/** The essence type an Essence skill belongs to, from its group on the tree; undefined for other skills. */
export function resolveSkillEssenceType(skillId: string): EssenceType | undefined {
    const entry = findSkillCatalogEntry(skillId);
    if (entry?.branch !== 'essence') {
        return undefined;
    }
    return entry.group.replace(/^essence-/, '') as EssenceType;
}

export interface WovenCast {
    /** What the woven abilities cost together. */
    readonly essenceCost: number;
    /** The traits each ability takes on, lent by the others' essence types, by ability id. */
    readonly traitsByAbilityId: Readonly<Record<string, readonly WeaveTrait[]>>;
}

/**
 * Abilities woven into one cast: each takes the traits lent by the others'
 * essence types, and together they cost `costFraction` of casting them apart.
 * Undefined unless every ability comes from a different essence type.
 */
export function resolveWeave(
    abilities: readonly EssenceAbilityDefinition[],
    costFraction = WEAVE_COST_FRACTION,
): WovenCast | undefined {
    const essenceTypes = abilities.map((ability) => resolveSkillEssenceType(ability.skillId));
    if (abilities.length < 2 || essenceTypes.some((essenceType) => essenceType === undefined) || new Set(essenceTypes).size !== essenceTypes.length) {
        return undefined;
    }
    const traitsByAbilityId: Record<string, WeaveTrait[]> = {};
    abilities.forEach((ability, index) => {
        traitsByAbilityId[ability.id] = essenceTypes
            .filter((_, otherIndex) => otherIndex !== index)
            .map((essenceType) => WEAVE_TRAIT_BY_ESSENCE_TYPE[essenceType as EssenceType]);
    });
    const fullCost = abilities.reduce((total, ability) => total + ability.essenceCost, 0);
    return { essenceCost: Math.round(fullCost * costFraction), traitsByAbilityId };
}

/** What casting `ability` costs a character with `skills`: changes to every ability (Efficient) lower it first. */
export function resolveAbilityEssenceCost(
    ability: EssenceAbilityDefinition,
    skills: readonly Skill[] | undefined,
    equipped?: CharacterEquipmentLoadout,
): number {
    const everyCost = resolveEveryAbilityChange(skills, equipped).costMultiplier;
    const perkCost = everyCost !== undefined
        ? Math.round(ability.essenceCost * everyCost)
        : ability.essenceCost;
    // Equipped items can lighten one essence type's abilities (`thrift-*` affixes).
    const essenceType = resolveSkillEssenceType(ability.skillId);
    const itemReduction = essenceType ? resolveItemAbilityCostReduction(equipped, essenceType) : 0;
    const reduced = itemReduction > 0 ? Math.round(perkCost * (1 - itemReduction)) : perkCost;
    // A perk or a named item can reshape one ability outright.
    const multiplier = resolveAbilityChange(ability, skills, equipped).costMultiplier;
    return multiplier !== undefined ? Math.max(0, Math.round(reduced * multiplier)) : reduced;
}

/**
 * Whether a character holding `essence` can pay an Essence ability's `cost`.
 * Essence is also life, so a cast must leave some: the pool has to hold more
 * than the cost. A free ability can always be paid.
 */
export function canPayAbilityEssenceCost(essence: number, cost: number): boolean {
    return cost <= 0 || essence > cost;
}

/** How long `ability` cools down for a character: equipped items, and any perk that changes it, can shorten it. */
export function resolveAbilityCooldownMs(
    ability: EssenceAbilityDefinition,
    skills: readonly Skill[] | undefined,
    equipped?: CharacterEquipmentLoadout,
): number {
    // Equipped items can quicken one essence type's abilities (`focus-*` affixes).
    const essenceType = resolveSkillEssenceType(ability.skillId);
    const itemReduction = essenceType ? resolveItemAbilityCooldownReduction(equipped, essenceType) : 0;
    const reduced = itemReduction > 0 ? Math.round(ability.cooldownMs * (1 - itemReduction)) : ability.cooldownMs;
    // A perk or a named item can reshape one ability outright.
    const multiplier = resolveAbilityChange(ability, skills, equipped).cooldownMultiplier;
    return multiplier !== undefined ? Math.max(0, Math.round(reduced * multiplier)) : reduced;
}
